import {
  object,
  array,
  number,
  boolean,
  string,
  SchemaOf,
  StringSchema,
} from 'yup';

import { DockerCleanupFormValues } from './types';
import { isValidDuration, parseDuration } from './duration';

const MIN_CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

function durationString(fieldLabel: string) {
  return string()
    .required(`${fieldLabel} is required`)
    .test(
      'valid-duration',
      `${fieldLabel} must be a valid duration (e.g. "3d", "24h", "30m")`,
      (v) => !!v && isValidDuration(v)
    );
}

export function createDockerCleanupValidationSchema(
  nameValidation: StringSchema<string>
): SchemaOf<DockerCleanupFormValues> {
  return object({
    name: nameValidation,
    environmentGroupIds: array(number().required()),
    cleanupInterval: durationString('Check interval').test(
      'minimum-duration',
      'Check interval must be at least 5 minutes',
      (v) => !!v && parseDuration(v) >= MIN_CLEANUP_INTERVAL_MS
    ),
    minAge: durationString('Minimum age'),
    maxImagesPerInterval: number()
      .min(0, 'Max images per pass cannot be negative')
      .required(),
    excludedImages: array(
      string()
        .default('')
        .test('valid-image-ref', '', (value, { createError }) => {
          if (!value || !value.trim()) return true;
          const v = value.trim();
          if (v.endsWith(':')) {
            return createError({
              message:
                'Image reference cannot end with ":". Add a tag name or remove the colon',
            });
          }
          if (v.endsWith('/')) {
            return createError({
              message:
                'Image reference cannot end with "/". Remove the trailing slash',
            });
          }
          // Allow: letters, digits, . _ - / : @  (covers registry:port/path:tag and @sha256 digests)
          if (!/^[a-zA-Z0-9][a-zA-Z0-9._/:@-]*$/.test(v)) {
            return createError({
              message:
                'Image reference contains invalid characters. Use letters, digits, ".", "-", "_", "/" for paths, and ":" for a tag.',
            });
          }
          return true;
        })
    ).required(),
    cleanOldImages: boolean().required(),
    imageMaximumGCAge: durationString('Max image age').when('cleanOldImages', {
      is: true,
      then: (schema) =>
        schema.test(
          'positive-duration',
          'Max image age must be greater than 0',
          (v) => !!v && parseDuration(v) > 0
        ),
    }),
    cleanImagesAtStorageLimit: boolean().required(),
    highThresholdPercent: number()
      .min(0)
      .max(100, 'High threshold cannot exceed 100%')
      .when('cleanImagesAtStorageLimit', {
        is: true,
        then: (schema) => schema.min(1, 'High threshold must be at least 1%'),
      })
      .required(),
    lowThresholdPercent: number()
      .min(0, 'Low threshold cannot be negative')
      .max(100)
      .when('cleanImagesAtStorageLimit', {
        is: true,
        then: (schema) =>
          schema.max(99, 'Low threshold must be less than 100%').test(
            'less-than-high',
            'Low threshold must be less than the high threshold',
            // eslint-disable-next-line func-names
            function (value) {
              return value == null || value < this.parent.highThresholdPercent;
            }
          ),
      })
      .required(),
    clearBuildCache: boolean().required(),
  }).test(
    'at-least-one-enabled',
    'At least one cleanup mode must be enabled',
    // eslint-disable-next-line func-names
    function (value) {
      if (value && (value.cleanOldImages || value.cleanImagesAtStorageLimit)) {
        return true;
      }
      return this.createError({
        path: 'cleanOldImages',
        message:
          'Please enable at least one cleanup mode: age-based cleanup or storage-limit cleanup.',
      });
    }
  );
}
